import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import { setPricingPrefs } from '../services/priceSource';

const ProfileContext = createContext({});

export const useProfile = () => useContext(ProfileContext);

export const ProfileProvider = ({ children }) => {
  const { user } = useAuth();

  // Current user's profile data from users table
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // A profile fetch that fails used to log and go quiet, leaving the profile
  // null for ever. Screens that wait on a user id then wait for ever too - the
  // home feed sat on skeletons with nothing to say and nothing to retry. Keep
  // the failure so callers can tell "no profile yet" apart from "no profile".
  const [profileError, setProfileError] = useState(null);

  // For parents: which profile are they viewing as (null = their own, or kid's user_id)
  // Initialize from localStorage if available (will be validated after profile loads)
  const [activeProfileId, setActiveProfileId] = useState(() => {
    const stored = localStorage.getItem('activeProfileId');
    return stored || null;
  });

  // Kids linked to this user (if they're a parent)
  const [linkedKids, setLinkedKids] = useState([]);

  // Parents linked to this user (if they're a kid)
  const [linkedParents, setLinkedParents] = useState([]);

  // Fetch user profile and family links
  const fetchProfile = useCallback(async () => {
    if (!user?.id) {
      setUserProfile(null);
      setLinkedKids([]);
      setLinkedParents([]);
      setLoading(false);
      return;
    }

    try {
      // Get user profile
      const { data: profile, error: fetchErr } = await supabase
        .from('users')
        .select('*')
        .eq('auth_id', user.id)
        .single();

      // PGRST116 is "no row", which is a real answer: this user has no profile
      // yet. Anything else is a failure and must not read as an empty profile.
      if (fetchErr && fetchErr.code !== 'PGRST116') {
        console.error('Error fetching profile:', fetchErr);
        setProfileError(fetchErr);
        setUserProfile(null);
        return;
      }

      setProfileError(null);
      setUserProfile(profile || null);

      if (profile?.id) {
        // If user is a parent, fetch linked kids
        if (profile.is_parent) {
          const { data: kids } = await supabase
            .from('family_links')
            .select(`
              child_id,
              can_trade,
              users!family_links_child_id_fkey (
                id, first_name, last_name, username, avatar_url, hide_profile,
                latitude, longitude, location_city, location_state, is_under_18,
                pricing_trigger, pricing_enabled
              )
            `)
            .eq('parent_id', profile.id);

          setLinkedKids(kids || []);
        }

        // If user is under 18, fetch linked parents
        if (profile.is_under_18) {
          const { data: parents } = await supabase
            .from('family_links')
            .select(`
              parent_id,
              can_trade,
              users:public_profiles!family_links_parent_id_fkey (
                id, first_name, last_name, username
              )
            `)
            .eq('child_id', profile.id);

          setLinkedParents(parents || []);
        }
      }
    } catch (err) {
      console.error('Error in fetchProfile:', err);
      setProfileError(err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Fetch on mount and when user changes
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Validate and restore activeProfileId after profile and linkedKids load
  useEffect(() => {
    if (loading || !userProfile) return;

    // Only parents can have an activeProfileId (viewing as a child)
    if (!userProfile.is_parent) {
      // Not a parent - clear any stored activeProfileId
      if (activeProfileId) {
        setActiveProfileId(null);
        localStorage.removeItem('activeProfileId');
      }
      return;
    }

    // Parent - validate the stored activeProfileId
    const storedId = localStorage.getItem('activeProfileId');
    const dbDefaultId = userProfile.default_profile_id;

    // Determine which profile to use (localStorage takes priority, then DB)
    const targetProfileId = storedId || dbDefaultId;

    if (targetProfileId) {
      // Check if this is the parent's own ID (viewing as self)
      if (targetProfileId === userProfile.id) {
        // Viewing as self - activeProfileId should be null or own ID
        if (activeProfileId !== targetProfileId && activeProfileId !== null) {
          setActiveProfileId(targetProfileId);
        }
        return;
      }

      // Check if targetProfileId is a valid linked child
      // IMPORTANT: Only validate if linkedKids has actually been loaded (is_parent means they should have kids data)
      // If linkedKids is empty, DON'T clear - the data might not be loaded yet
      const isValidChild = linkedKids.some(k => k.child_id === targetProfileId);

      if (isValidChild) {
        // Valid child - restore if not already set
        if (activeProfileId !== targetProfileId) {
          setActiveProfileId(targetProfileId);
        }
      } else if (linkedKids.length > 0) {
        // Only clear if we have linkedKids data but the child isn't in it
        // This prevents clearing when linkedKids is just empty/loading
        if (activeProfileId) {
          setActiveProfileId(null);
          localStorage.removeItem('activeProfileId');
        }
      }
      // If linkedKids is empty, do nothing - preserve activeProfileId until data loads
    }
  }, [loading, userProfile, linkedKids, activeProfileId]);

  // Switch to viewing as a different profile (for parents)
  const switchProfile = useCallback((profileId) => {
    // null = view as self
    // profileId = view as that kid
    setActiveProfileId(profileId);
    // Store in localStorage for persistence
    if (profileId) {
      localStorage.setItem('activeProfileId', profileId);
    } else {
      localStorage.removeItem('activeProfileId');
    }
  }, []);

  // Get the currently active profile data
  // If parent is viewing as kid, return kid's data; otherwise return own profile
  // IMPORTANT: This must be computed BEFORE canTrade/isViewerOnly since they depend on it
  const activeProfile = (() => {
    if (!activeProfileId || !userProfile?.is_parent) {
      return userProfile;
    }

    // Find the kid's profile in linkedKids
    const kidLink = linkedKids.find(k => k.child_id === activeProfileId);
    if (kidLink?.users) {
      return kidLink.users;
    }

    // If activeProfileId doesn't match a child (e.g., it's the parent's own ID),
    // fall back to the parent's own profile
    return userProfile;
  })();

  // Derived: Can the ACTIVE profile trade?
  const canTrade = (() => {
    if (!activeProfile) return false;

    // PRIMARY RULE: Adults ALWAYS have full access, no exceptions
    // This covers: adult non-parents, parents viewing as themselves
    if (!activeProfile.is_under_18) return true;

    // MINOR RULES: Active profile is a minor, check specific permissions

    // Case 1: Parent viewing as child - check that child's family_link permission
    if (activeProfileId && userProfile?.is_parent) {
      const kidLink = linkedKids.find(k => k.child_id === activeProfileId);
      return kidLink?.can_trade === true;
    }

    // Case 2: Minor logged in directly - check if any parent approved trading
    if (linkedParents.length > 0) {
      return linkedParents.some(link => link.can_trade === true);
    }

    // Case 3: Minor with no parents linked cannot trade
    return false;
  })();

  // Derived: Is the ACTIVE profile in viewer-only mode?
  const isViewerOnly = (() => {
    if (!activeProfile) return true; // Not loaded yet, assume restricted

    // PRIMARY RULE: Adults are NEVER viewer-only, no exceptions
    // This covers: adult non-parents, parents viewing as themselves
    if (!activeProfile.is_under_18) return false;

    // MINOR RULES: Active profile is a minor, check specific restrictions

    // Minor who explicitly chose not to trade (wants_to_trade = false)
    if (activeProfile.wants_to_trade === false) return true;

    // Minor who wants to trade but doesn't have permission yet
    return !canTrade;
  })();

  // Publish the account's pricing preferences to the price cache.
  //
  // The price selection sits several layers below any screen, and most of its
  // callers have no profile in hand: the collection, the bulk scanner and the
  // vendor deal all read prices with a null prefs. They would each have priced
  // off the default cut while the card screen used the owner's, which is the
  // one thing this preference exists to stop. One publisher, every reader.
  useEffect(() => {
    setPricingPrefs(activeProfile?.pricing_prefs || null);
  }, [activeProfile?.pricing_prefs]);

  // Refresh profile data (call after updates)
  // Returns Promise so callers can await completion
  const refreshProfile = useCallback(async () => {
    setLoading(true);
    return fetchProfile();
  }, [fetchProfile]);

  // Rebuilt on every render, this handed every consumer of useProfile a new
  // object each time the provider rendered, so all of them re-rendered with it.
  const value = useMemo(() => ({
    // User's own profile
    userProfile,
    // Currently active profile (for parent viewing as kid)
    activeProfile,
    activeProfileId,
    // Family links
    linkedKids,
    linkedParents,
    // Permissions
    canTrade,
    isViewerOnly,
    // Actions
    switchProfile,
    refreshProfile,
    // State
    loading,
    profileError,
  }), [
    userProfile, activeProfile, activeProfileId, linkedKids, linkedParents,
    canTrade, isViewerOnly, switchProfile, refreshProfile, loading, profileError,
  ]);

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};
